import React, { useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { useQuery } from 'react-query';
import { getProduct } from '../services/productService';
import ProductForm from '../components/forms/ProductForm';
import Card from '../components/Card/Card';
import CardHeader from '../components/Card/CardHeader';
import CardBody from '../components/Card/CardBody';

export default function ProductFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const textColor = useColorModeValue('gray.700', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('gray.50', 'gray.800');
  
  const [isDirty, setIsDirty] = useState(false);

  const isEdit = Boolean(id);
  const { data: productData, isLoading, error } = useQuery(
    ['product', id],
    () => getProduct(id),
    { enabled: isEdit }
  );
  const product = productData?.data ?? null;

  const handleSave = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const handleSuccess = () => {
    navigate('/products');
  };

  if (isEdit && isLoading) {
    return (
      <Flex direction="column" minH="50vh" justify="center" align="center" gap={4}>
        <Spinner size="xl" color="teal.500" />
        <Text color={mutedColor}>در حال بارگذاری محصول...</Text>
      </Flex>
    );
  }

  if (isEdit && (error || !product)) {
    return (
      <Box>
        <Alert status="error" borderRadius="lg" mb={4}>
          <AlertIcon />
          <Box>
            <AlertTitle>محصول یافت نشد</AlertTitle>
            <AlertDescription>
              {error?.response?.data?.message || 'این محصول وجود ندارد یا دسترسی به آن ندارید.'}
            </AlertDescription>
          </Box>
        </Alert>
        <Button as={RouterLink} to="/products" leftIcon={<ChevronLeftIcon />} colorScheme="teal" variant="outline">
          بازگشت به لیست محصولات
        </Button>
      </Box>
    );
  }

  return (
    <Box maxW="100%" mx="auto" px={{ base: 2, md: 4 }} minW={0} overflow="hidden">
      {/* Breadcrumb & Back */}
      <Flex align="center" gap={3} mb={6} flexWrap="wrap">
        <Button
          as={RouterLink}
          to="/products"
          leftIcon={<ChevronLeftIcon />}
          variant="ghost"
          size="sm"
          color={mutedColor}
          _hover={{ color: 'teal.500', bg: 'teal.50' }}
        >
          محصولات
        </Button>
        <Text color={mutedColor} fontSize="sm">/</Text>
        <Text fontSize="sm" fontWeight="600" color={textColor}>
          {isEdit ? 'ویرایش محصول' : 'افزودن محصول جدید'}
        </Text>
      </Flex>

      {/* Card */}
      <Card bg={cardBg} boxShadow="lg" borderRadius="xl" overflow="visible" borderWidth="1px" borderColor={borderColor}>
        <CardHeader
          py={6}
          px={8}
          borderBottomWidth="1px"
          borderColor={borderColor}
          bg={headerBg}
        >
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <Box>
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                {isEdit ? 'ویرایش محصول' : 'افزودن محصول جدید'}
              </Text>
              <Text fontSize="sm" color={mutedColor} mt={1}>
                {isEdit
                  ? 'نقشه اتصال تنوع‌ها را در چارت سازمانی زیر ویرایش کنید.'
                  : 'نام، کد حسابداری، تنوع‌های منطقی و درگاه‌ها را وارد کنید.'}
              </Text>
            </Box>
            <Flex gap={2}>
              <Button
                as={RouterLink}
                to="/products"
                variant="outline"
                size="md"
              >
                انصراف
              </Button>
              <Button colorScheme="teal" size="md" onClick={handleSave}>
                ذخیره محصول
              </Button>
            </Flex>
          </Flex>
        </CardHeader>
        <CardBody p={{ base: 4, md: 8 }} maxW="100%" minW={0} overflow="hidden">
          <ProductForm 
            ref={formRef} 
            product={product} 
            onSuccess={handleSuccess} 
            onDirty={() => setIsDirty(true)} 
          />
        </CardBody>
      </Card>

      {/* Floating Action Button (FAB) برای ذخیره */}
      {isDirty && (
        <Box
          position="fixed"
          bottom={8}
          left={8}
          zIndex={1000}
          animation="pulse 2s infinite"
          sx={{
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(49, 151, 149, 0.7)' },
              '70%': { transform: 'scale(1.05)', boxShadow: '0 0 0 15px rgba(49, 151, 149, 0)' },
              '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(49, 151, 149, 0)' },
            }
          }}
        >
          <Button
            size="lg"
            colorScheme="teal"
            shadow="dark-lg"
            borderRadius="full"
            px={8}
            onClick={handleSave}
          >
            تغییرات را ثبت کنید
          </Button>
        </Box>
      )}
    </Box>
  );
}
